import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectRecentModules, moduleMaturity } from '../../common-showcase';

/**
 * RecentModulesCard — queue #658 §UAT-2026-07-08-R658 · `_index.md` §R658-cross.
 *
 * 대시보드 '최근 변경 모듈' 카드 — common-showcase 가 public re-export 하는 순수 셀렉터
 * `selectRecentModules(moduleMaturity, 5)` 를 소비해 `lastChanged` 내림차순 상위 5 모듈을
 * 렌더한다. 레이아웃은 `MonitoringLinks` 카드 관례(카드+행 수직 배치)를 답습.
 * 신규 endpoint/BE 0 — §R643 커밋 `module-maturity.json` read-only 재파생.
 * 권한 무관(전 역할 노출 — 학습 콘텐츠, common-showcase `sidebarRole=ROLE_USER` 계보).
 */
export function RecentModulesCard() {
  const { t } = useTranslation();
  const topModules = selectRecentModules(moduleMaturity, 5);

  return (
    <div
      aria-label={t('_index.recentModules.title')}
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-4)',
        marginTop: 'var(--spacing-4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {t('_index.recentModules.title')}
        </h2>
        <Link
          to="/showcase"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-brand-primary)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {t('_index.recentModules.viewAll')}
        </Link>
      </div>

      {topModules.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          {t('_index.recentModules.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          {topModules.map((mod) => (
            <li key={mod.artifact}>
              {/*
                #658 §R658-cross: 딥링크는 "/showcase#<firstCode>" 가 원칙이나, 본 카드는
                common-showcase 의 BE 카탈로그(ModuleNode[])를 재-fetch 하지 않고 정적
                module-maturity.json 만 소비한다(신규 endpoint/네트워크 의존 0 원칙) — 즉
                artifact→code 매핑이 항상 "노드 매핑 불가" 상태이므로 `?q=<artifact>` 검색
                필터 폴백을 사용한다(§R658-2① RecentChangesPanel 과 동일 폴백 규칙 적용).
              */}
              <Link
                to={`/showcase?q=${encodeURIComponent(mod.artifact)}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-surface-border)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-primary)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <span style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>
                  {mod.artifact}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {mod.lastChanged ?? '—'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
